import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Wrench, ExternalLink, Menu, X } from "lucide-react";
import { useState } from "react";
import itdukesLogo from "@/assets/itdukes-logo.png";

interface InsightsNavbarProps {
  showBack?: boolean;
}

export const InsightsNavbar = ({ showBack = false }: InsightsNavbarProps) => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isTools = location.pathname === "/tools";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/80 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-18">
          {/* Left side - Logo & Brand */}
          <div className="flex items-center gap-4">
            {showBack && !isHome ? (
              <>
                <Link 
                  to="/" 
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition-all duration-200"
                  aria-label="Back to all insights"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Link>
                <div className="w-px h-8 bg-slate-200 hidden sm:block" />
              </>
            ) : null}
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                src={itdukesLogo} 
                alt="IT Dukes" 
                width={141}
                height={40}
                className="h-9 sm:h-10 w-auto"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden sm:flex items-center gap-1">
            <Link 
              to="/"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                isHome 
                  ? "bg-primary text-white shadow-md shadow-primary/25" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              <span>Insights</span>
            </Link>
            <Link 
              to="/tools"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                isTools 
                  ? "bg-primary text-white shadow-md shadow-primary/25" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              <Wrench className="w-4 h-4" />
              <span>Tools</span>
            </Link>
            <div className="w-px h-6 bg-slate-200 mx-2" />
            <a 
              href="https://itdukes.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-slate-600 hover:text-primary hover:bg-primary/5 transition-all duration-200 border border-transparent hover:border-primary/20"
            >
              <span>Visit ITDukes.com</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="sm:hidden flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition-all duration-200"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-slate-200/80 py-4 space-y-2 animate-fade-in">
            <Link 
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                isHome 
                  ? "bg-primary text-white shadow-md" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              Insights
            </Link>
            <Link 
              to="/tools"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                isTools 
                  ? "bg-primary text-white shadow-md" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              <Wrench className="w-4 h-4" />
              Tools
            </Link>
            <a 
              href="https://itdukes.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-600 hover:text-primary hover:bg-primary/5 transition-all"
            >
              Visit ITDukes.com
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};
